import type {
  ExtrudeGeometry,
  Group,
  Mesh,
  MeshPhysicalMaterial,
  Path,
  PerspectiveCamera,
  Scene,
  Vector2,
  WebGLRenderer,
} from 'three';

/** Brand primary green (#169E51), matching Stitch Kinetic / shader background. */
const BRAND_PRIMARY_GREEN = 0x169e51;

type LogoPoint = readonly [number, number];

/** Outer green diamond frame outline (svg-space coordinates, 710x710 viewbox). */
const GREEN_OUTLINE: readonly LogoPoint[] = [
  [140, 71], [150, 67], [157, 67], [165, 70], [211, 117], [213, 117],
  [319, 11], [338, 2], [363, 0], [376, 3], [393, 12], [499, 119],
  [501, 119], [547, 72], [554, 69], [564, 69], [571, 72], [578, 79],
  [580, 84], [580, 96], [577, 103], [530, 149], [560, 180], [608, 133],
  [615, 130], [625, 130], [632, 133], [639, 140], [641, 145], [641, 157],
  [637, 165], [591, 211], [698, 317], [708, 337], [710, 346], [710, 363],
  [704, 383], [696, 395], [593, 497], [593, 499], [640, 545], [643, 552],
  [643, 562], [640, 569], [633, 576], [623, 579], [615, 578], [609, 575],
  [563, 528], [532, 558], [532, 560], [579, 606], [582, 613], [582, 623],
  [576, 634], [570, 638], [562, 640], [554, 639], [548, 636], [501, 589],
  [395, 696], [388, 701], [371, 708], [358, 710], [344, 709], [333, 706],
  [317, 697], [212, 591], [210, 591], [164, 638], [157, 641], [146, 641],
  [138, 637], [134, 633], [130, 623], [130, 616], [133, 608], [180, 562],
  [180, 559], [151, 530], [149, 530], [103, 577], [96, 580], [85, 580],
  [73, 572], [69, 562], [69, 555], [72, 547], [119, 501], [119, 498],
  [14, 394], [3, 373], [1, 364], [1, 345], [5, 331], [17, 312],
  [121, 209], [74, 162], [71, 155], [71, 145], [75, 136], [79, 132],
  [89, 128], [96, 128], [104, 131], [150, 178], [152, 178], [182, 148],
  [135, 101], [132, 94], [132, 83],
];

/** Inner diamond (hole in the green frame / face plate of the mark). */
const INNER_DIAMOND: readonly LogoPoint[] = [
  [350, 43], [344, 46], [47, 343], [44, 349], [44, 361], [47, 367],
  [343, 663], [348, 666], [359, 667], [369, 662], [662, 369], [667, 360],
  [667, 350], [664, 343], [367, 46], [360, 43],
];

/** The "W" letterform sitting on the inner diamond. */
const LETTER_W: readonly LogoPoint[] = [
  [225, 278], [279, 278], [306, 401], [338, 278], [375, 278], [406, 401],
  [434, 278], [488, 278], [435, 464], [384, 464], [356, 347], [329, 464],
  [278, 464],
];

const LOGO_SCALE = 0.0093;

export type HeroThreeSceneHandle = {
  resize: (width: number, height: number) => void;
  setPaused: (paused: boolean) => void;
  dispose: () => void;
};

/**
 * Creates the Kinetic hero Three.js logo (extruded Wilkins Software mark).
 * Rotation follows the pointer position across the viewport with a gentle
 * idle drift and damped easing. Dynamically imports `three` so it stays out
 * of the SSR/worker module graph until this factory runs on the client.
 */
export async function createHeroThreeScene(
  container: HTMLElement,
): Promise<HeroThreeSceneHandle | null> {
  let THREE: typeof import('three');
  try {
    THREE = await import('three');
  } catch {
    return null;
  }

  const mapContour = (contour: readonly LogoPoint[]): Vector2[] =>
    contour.map(
      ([x, y]) =>
        new THREE.Vector2((x - 355) * LOGO_SCALE, (355 - y) * LOGO_SCALE),
    );

  const orderedPoints = (
    contour: readonly LogoPoint[],
    clockwise: boolean,
  ): Vector2[] => {
    const points = mapContour(contour);
    return THREE.ShapeUtils.isClockWise(points) === clockwise
      ? points
      : points.reverse();
  };

  const drawClosedPath = <PathType extends Path>(
    path: PathType,
    points: Vector2[],
  ): PathType => {
    path.moveTo(points[0].x, points[0].y);
    for (let pointIndex = 1; pointIndex < points.length; pointIndex += 1) {
      path.lineTo(points[pointIndex].x, points[pointIndex].y);
    }
    path.closePath();
    return path;
  };

  const width = container.clientWidth || 1;
  const height = container.clientHeight || 1;

  let scene: Scene | null = new THREE.Scene();
  let camera: PerspectiveCamera | null = new THREE.PerspectiveCamera(
    33,
    width / height,
    0.1,
    100,
  );
  camera.position.set(0, 0, 12);

  let renderer: WebGLRenderer | null;
  try {
    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  } catch {
    scene = null;
    camera = null;
    return null;
  }

  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;
  // Keep updateStyle true so DPR buffer size does not inflate layout.
  renderer.setSize(width, height, true);
  renderer.domElement.style.display = 'block';
  renderer.domElement.setAttribute('aria-hidden', 'true');
  container.appendChild(renderer.domElement);

  let model: Group | null = new THREE.Group();
  scene.add(model);

  const greenMaterial: MeshPhysicalMaterial = new THREE.MeshPhysicalMaterial({
    color: BRAND_PRIMARY_GREEN,
    metalness: 0.14,
    roughness: 0.27,
    clearcoat: 0.72,
    clearcoatRoughness: 0.2,
  });
  const blackMaterial: MeshPhysicalMaterial = new THREE.MeshPhysicalMaterial({
    color: 0x030604,
    metalness: 0.12,
    roughness: 0.35,
    clearcoat: 0.28,
    clearcoatRoughness: 0.35,
  });
  const whiteMaterial: MeshPhysicalMaterial = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    metalness: 0.04,
    roughness: 0.22,
    clearcoat: 0.55,
    clearcoatRoughness: 0.18,
  });

  const greenShape = drawClosedPath(
    new THREE.Shape(),
    orderedPoints(GREEN_OUTLINE, true),
  );
  greenShape.holes.push(
    drawClosedPath(new THREE.Path(), orderedPoints(INNER_DIAMOND, false)),
  );
  const blackShape = drawClosedPath(
    new THREE.Shape(),
    orderedPoints(INNER_DIAMOND, true),
  );
  const letterShape = drawClosedPath(
    new THREE.Shape(),
    orderedPoints(LETTER_W, true),
  );

  const greenGeometry: ExtrudeGeometry = new THREE.ExtrudeGeometry(greenShape, {
    depth: 0.64,
    bevelEnabled: true,
    bevelSegments: 5,
    bevelSize: 0.085,
    bevelThickness: 0.12,
    curveSegments: 12,
  });
  greenGeometry.translate(0, 0, -0.32);

  const blackGeometry: ExtrudeGeometry = new THREE.ExtrudeGeometry(blackShape, {
    depth: 0.36,
    bevelEnabled: true,
    bevelSegments: 4,
    bevelSize: 0.045,
    bevelThickness: 0.055,
    curveSegments: 12,
  });
  blackGeometry.translate(0, 0, -0.3);

  const letterGeometry: ExtrudeGeometry = new THREE.ExtrudeGeometry(
    letterShape,
    {
      depth: 0.32,
      bevelEnabled: true,
      bevelSegments: 4,
      bevelSize: 0.035,
      bevelThickness: 0.045,
      curveSegments: 8,
    },
  );
  letterGeometry.translate(0, 0, 0.06);

  const logoGeometries: ExtrudeGeometry[] = [
    greenGeometry,
    blackGeometry,
    letterGeometry,
  ];
  for (const geometry of logoGeometries) {
    geometry.computeVertexNormals();
  }

  const greenMesh: Mesh = new THREE.Mesh(greenGeometry, greenMaterial);
  const blackMesh: Mesh = new THREE.Mesh(blackGeometry, blackMaterial);
  const letterMesh: Mesh = new THREE.Mesh(letterGeometry, whiteMaterial);
  blackMesh.position.z = -0.08;

  // Shrink the black diamond about the shared center so a green gap ring shows
  // between the outer frame's inner edge and the diamond, matching the real
  // Wilkins Software mark. The "W" keeps its own (larger) scale so only the
  // diamond gap widens. Both geometries are centered on the origin, so scaling
  // preserves their relative arrangement.
  blackMesh.scale.setScalar(0.688);
  letterMesh.scale.setScalar(0.86);
  model.add(greenMesh, blackMesh, letterMesh);

  const hemisphereLight = new THREE.HemisphereLight(0xdfffea, 0x001a0c, 1.55);
  scene.add(hemisphereLight);

  const keyLight = new THREE.DirectionalLight(0xffffff, 4.6);
  keyLight.position.set(5.5, 7.5, 9);
  scene.add(keyLight);

  const rimLight = new THREE.PointLight(0x18e979, 38, 18, 2);
  rimLight.position.set(-4.5, -2.4, 3.5);
  scene.add(rimLight);

  const topRim = new THREE.PointLight(0x76ffb4, 24, 15, 2);
  topRim.position.set(4.5, 4.5, -0.3);
  scene.add(topRim);

  const baseRotationX = -0.11;
  const baseRotationY = 0.22;
  model.rotation.set(baseRotationX, baseRotationY, 0);

  /**
   * Tall+narrow desktop columns can clip the rotated mark — scale down only
   * in that case. Short stacked-hero canvases already size the mark via the
   * element dimensions, so keep full scale there (avoids a tiny mark in a
   * padded box).
   */
  const FULL_SCALE_MIN_CONTAINER_WIDTH = 380;
  const TALL_COLUMN_MIN_HEIGHT = 300;
  const applyContainerFitScale = (
    containerWidth: number,
    containerHeight: number,
  ) => {
    if (!model) {
      return;
    }
    if (
      containerHeight < TALL_COLUMN_MIN_HEIGHT ||
      containerWidth >= FULL_SCALE_MIN_CONTAINER_WIDTH
    ) {
      model.scale.setScalar(1);
      return;
    }
    const fitScale = Math.max(
      0.78,
      containerWidth / FULL_SCALE_MIN_CONTAINER_WIDTH,
    );
    model.scale.setScalar(fitScale);
  };
  applyContainerFitScale(width, height);

  // Pointer-driven rotation offset (normalized -1..1 from viewport center).
  const pointerRotation = { x: 0, y: 0 };

  let animationFrameId: number | null = null;
  let isPaused = false;
  let isDisposed = false;
  let lastFrameTime = performance.now();

  const handlePointerMove = (event: PointerEvent) => {
    if (isDisposed) {
      return;
    }
    const bounds = container.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) {
      return;
    }
    const normalizedX =
      ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    const normalizedY =
      ((event.clientY - bounds.top) / bounds.height) * 2 - 1;
    // Clamp so far-away cursor positions do not over-rotate the mark.
    pointerRotation.x = -Math.max(-1.5, Math.min(1.5, normalizedY)) * 0.28;
    pointerRotation.y = Math.max(-1.5, Math.min(1.5, normalizedX)) * 0.45;
  };

  // Container is pointer-events-none, so listen on the window for movement.
  window.addEventListener('pointermove', handlePointerMove, { passive: true });

  const stopRenderLoop = () => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
  };

  const renderFrame = () => {
    if (isDisposed || isPaused || !model || !renderer || !scene || !camera) {
      animationFrameId = null;
      return;
    }
    const now = performance.now();
    const delta = Math.min((now - lastFrameTime) / 1000, 0.05);
    lastFrameTime = now;
    const elapsed = now / 1000;

    const idleX = Math.sin(elapsed * 0.72) * 0.025;
    const idleY = Math.sin(elapsed * 0.46) * 0.055;
    const targetX = baseRotationX + pointerRotation.x + idleX;
    const targetY = baseRotationY + pointerRotation.y + idleY;
    const damping = 1 - Math.exp(-delta * 6.5);

    model.rotation.x = THREE.MathUtils.lerp(model.rotation.x, targetX, damping);
    model.rotation.y = THREE.MathUtils.lerp(model.rotation.y, targetY, damping);

    renderer.render(scene, camera);
    animationFrameId = requestAnimationFrame(renderFrame);
  };

  const startRenderLoop = () => {
    if (animationFrameId === null && !isPaused && !isDisposed) {
      lastFrameTime = performance.now();
      animationFrameId = requestAnimationFrame(renderFrame);
    }
  };

  const resize = (resizeWidth: number, resizeHeight: number) => {
    if (isDisposed || !camera || !renderer) {
      return;
    }
    if (resizeWidth <= 0 || resizeHeight <= 0) {
      return;
    }
    camera.aspect = resizeWidth / resizeHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(resizeWidth, resizeHeight, true);
    applyContainerFitScale(resizeWidth, resizeHeight);
  };

  const setPaused = (paused: boolean) => {
    if (isDisposed) {
      return;
    }
    isPaused = paused;
    if (paused) {
      stopRenderLoop();
    } else {
      startRenderLoop();
    }
  };

  const dispose = () => {
    if (isDisposed) {
      return;
    }
    isDisposed = true;
    stopRenderLoop();
    window.removeEventListener('pointermove', handlePointerMove);

    if (model) {
      model.clear();
      model = null;
    }

    for (const geometry of logoGeometries) {
      geometry.dispose();
    }

    greenMaterial.dispose();
    blackMaterial.dispose();
    whiteMaterial.dispose();

    if (renderer) {
      const canvasElement = renderer.domElement;
      renderer.dispose();
      if (canvasElement.parentNode === container) {
        container.removeChild(canvasElement);
      }
      renderer = null;
    }

    scene = null;
    camera = null;
  };

  startRenderLoop();

  return {
    resize,
    setPaused,
    dispose,
  };
}
